import { useMemo } from 'react'
import {
  accountHasPackageSlug,
  userHasFeature,
  type PackageFeatureId,
} from '@proffer/shared'
import { usePackageCatalog } from '../components/auth/PackageCatalogContext'
import { useAuth } from './AuthContext'

const GATE_FEATURE: Record<2 | 3 | 4, PackageFeatureId> = {
  2: 'public_profile',
  3: 'expenses',
  4: 'reports_nav_statistics',
}

export function useEntitlements() {
  const { user } = useAuth()
  const { catalog } = usePackageCatalog()

  const entitlements = useMemo(
    () => user?.entitlements ?? [],
    [user?.entitlements],
  )

  const activePackages = useMemo(
    () => user?.activePackages ?? [],
    [user?.activePackages],
  )

  const hasFeature = useMemo(() => {
    return (featureId: PackageFeatureId) => {
      if (!user) return false
      if (entitlements.length > 0) {
        return userHasFeature(entitlements, featureId)
      }
      return false
    }
  }, [user, entitlements])

  const hasPackage = useMemo(() => {
    return (slug: string) => {
      if (!user || catalog.length === 0) return false
      return accountHasPackageSlug(
        catalog,
        {
          plan: user.plan,
          activePackageSlugs: activePackages.map((p) => p.slug),
        },
        slug,
      )
    }
  }, [user, catalog, activePackages])

  const hasPackageGate = useMemo(() => {
    return (gate: 2 | 3 | 4) => hasFeature(GATE_FEATURE[gate])
  }, [hasFeature])

  return { entitlements, activePackages, hasFeature, hasPackage, hasPackageGate, user }
}
